import { useLazyGetTemplateByIdQuery } from 'store/services/helios/templates';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled, Typography as MuiTypography } from '@mui/material';
import { toast } from 'react-toastify';

import { isUndefined } from 'utils/typeChecks';
import { isMobileApp } from 'utils/common';

import type { HeliosRecordDataTableColumnSettings } from 'types/dataHelios';

const Typography = styled(MuiTypography)(() => ({
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

interface Props {
  value?: string;
  dataType?: string;
  settings?: HeliosRecordDataTableColumnSettings;
}

const LinkBase = ({ value, settings }: Props) => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const navigate = useNavigate();

  const [getTemplateById] = useLazyGetTemplateByIdQuery();

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      try {
        const templateId = settings?.link?.detailTemplateId;
        if (!templateId) {
          throw new Error('Template ID not found in settings');
        }

        const { data: template } = await getTemplateById({ id: templateId });
        if (!template?.moduleId) {
          throw new Error('Module ID not found in template');
        }

        const url = `/user/modules/${template.moduleId}/Helios/${templateId}/r/${value}/detail`;

        if (mobileApp) {
          navigate(url);
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        toast.error(t('helios.listing.navigateError'));
      }
    },
    [getTemplateById, mobileApp, navigate, settings?.link?.detailTemplateId, value, t]
  );

  const hasValue = !isUndefined(value) && String(value).length > 0;

  if (!hasValue) {
    return <MuiTypography variant="subtitle2" />;
  }

  return (
    <Typography variant="subtitle2" onClick={handleClick}>
      {value}
    </Typography>
  );
};

export const LinkHelios = memo(LinkBase) as typeof LinkBase;
